import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, AvgChip, BatchStatus, ProgressBar, TwistButton, BATCH_STATUS, fmtScore, useEscape, useToast } from '../../components/ui';
import { batchAPI } from '../../services/api';
import { resultsForExamSet, statsFor } from './derive';
import { batchStatusOf, batchWindow, dateInput } from './batches';
import { Completion } from './InterviewsTab';
import AddCandidatesDialog from './AddCandidatesDialog';
import BatchCandidates from './BatchCandidates';

/* Batch pieces of the position workspace (Interviews table, side panel, Add candidates dialog). */

/*
 * Evaluation settings are per batch (backend app/services/eval_settings.py):
 *   grading_leniency 0 = strictest … 10 = most lenient (shown as strictness = 10 - leniency)
 *   min_hire_score   hire bar, 0–100 like every score in the workspace
 * They drive the AI interview, JD exam and language exam scoring of that batch's candidates.
 */
const clampInt = (v, lo, hi) => Math.min(hi, Math.max(lo, Math.round(Number(v))));

/** Position interview length, the same order as the backend (eval_settings.position_duration). */
const positionDuration = (c) => {
  const n = [c?.duration_override, c?.estimated_duration_minutes, c?.interview_duration]
    .map((v) => parseInt(v, 10)).find((v) => Number.isFinite(v) && v > 0);
  return n || 45;
};

/** Strictness (0–10), hire bar (0–100) and duration (minutes) a new batch of this interview starts from. */
export function newBatchDefaults(data, examSetId) {
  const list = data.batchesByExamSet[examSetId] || [];
  const last = list[list.length - 1];
  const leniency = last?.grading_leniency ?? data.campaign?.grading_leniency ?? 5;
  const bar = last?.min_hire_score ?? data.campaign?.min_hire_score ?? 50;
  const duration = last?.duration_minutes ?? positionDuration(data.campaign);
  return { strict: 10 - Number(leniency), hire: Math.round(Number(bar)), duration: Math.round(Number(duration)) };
}

/** "Strictness 5/10 · Hire bar 65/100 · 30 min". */
export function batchEvalText(b) {
  const strict = 10 - Number(b?.grading_leniency ?? 5);
  const minutes = b?.duration_minutes ? ` · ${b.duration_minutes} min` : '';
  return `Strictness ${strict}/10 · Hire bar ${fmtScore(b?.min_hire_score ?? 50)}/100${minutes}`;
}

const STRICT_LABELS = [[3, 'Lenient'], [6, 'Balanced'], [10, 'Strict']];
const strictLabel = (n) => STRICT_LABELS.find(([max]) => n <= max)[1];
/** 0 → green, 5 → amber, 10 → red: the same stops as the slider track. */
const strictColor = (n) => `hsl(${Math.round(130 - (Math.min(10, Math.max(0, n)) * 13))} 70% 38%)`;

/** Strictness slider (green lenient → red strict), hire score and interview duration, shared by
 *  "Start a new batch" and the batch settings dialog. */
function EvalFields({ strict, hire, duration, onStrict, onHire, onDuration }) {
  const n = Number.isFinite(Number(strict)) ? Math.min(10, Math.max(0, Math.round(Number(strict)))) : 5;
  return (
    <div className="wz-eval">
      <div className="wz-eval-strict">
        <div className="wz-eval-h">
          <span>Grading strictness</span>
          <b style={{ color: strictColor(n) }}>{n}/10 · {strictLabel(n)}</b>
        </div>
        <input
          type="range"
          className="wz-strict-range"
          min={0}
          max={10}
          step={1}
          value={n}
          aria-label="Grading strictness, 0 lenient to 10 strict"
          aria-valuetext={`${n} of 10, ${strictLabel(n)}`}
          style={{ '--thumb': strictColor(n) }}
          onChange={(e) => onStrict(e.target.value)}
        />
        <div className="wz-eval-ends"><span>Lenient</span><span>Strict</span></div>
      </div>
      <label className="wz-eval-hire">
        <span>Hire score</span>
        <span className="wz-eval-hire-in">
          <input type="number" min={0} max={100} step={1} value={hire} aria-label="Hire score out of 100" onChange={(e) => onHire(e.target.value)} />
          <span className="wz-sub">/100</span>
        </span>
      </label>
      <label className="wz-eval-hire">
        <span>Duration</span>
        <span className="wz-eval-hire-in">
          <input type="number" min={5} max={240} step={5} value={duration} aria-label="Interview duration in minutes" onChange={(e) => onDuration(e.target.value)} />
          <span className="wz-sub">min</span>
        </span>
      </label>
    </div>
  );
}

const evalError = (strict, hire, duration) => {
  const s = Number(strict);
  const h = Number(hire);
  const d = Number(duration);
  if (strict === '' || !Number.isInteger(s) || s < 0 || s > 10) return 'Grading strictness must be a whole number from 0 to 10.';
  if (hire === '' || !Number.isInteger(h) || h < 0 || h > 100) return 'Hire score must be a whole number from 0 to 100.';
  if (duration === '' || !Number.isInteger(d) || d < 5 || d > 240) return 'Duration must be a whole number of minutes from 5 to 240.';
  return '';
};

/** Edit one batch's grading strictness and hire score. */
export function BatchSettingsDialog({ campaign, examSet, batch, onClose, onSaved }) {
  const boxRef = useRef(null);
  useEscape(onClose, { ref: boxRef });
  const toast = useToast();
  const [strict, setStrict] = useState(String(10 - Number(batch.grading_leniency ?? 5)));
  const [hire, setHire] = useState(String(Math.round(Number(batch.min_hire_score ?? 50))));
  const [duration, setDuration] = useState(String(batch.duration_minutes ?? 45));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { boxRef.current?.querySelector('input')?.focus(); }, []);

  const save = async () => {
    const msg = evalError(strict, hire, duration);
    if (msg) { setError(msg); return; }
    const leniency = 10 - clampInt(strict, 0, 10);
    const bar = clampInt(hire, 0, 100);
    const minutes = clampInt(duration, 5, 240);
    if (leniency === batch.grading_leniency && bar === batch.min_hire_score && minutes === batch.duration_minutes) { onClose(); return; }
    setSaving(true);
    setError('');
    try {
      await batchAPI.update(campaign.id, examSet.id, batch.id, { grading_leniency: leniency, min_hire_score: bar, duration_minutes: minutes });
      toast(bar !== batch.min_hire_score
        ? `${batch.name} saved. Re-labelling its finished candidates against ${bar}/100 — give it a moment before re-opening a report.`
        : `${batch.name} saved.`);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save the batch settings.');
      setSaving(false);
    }
  };

  return (
    <>
      <div className="wz-scrim" onClick={onClose} />
      <div className="wz-dialog" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div ref={boxRef} className="wz-dialog-box" role="dialog" aria-modal="true" aria-label={`Evaluation settings for ${batch.name}`} style={{ width: 'min(480px, 100%)' }}>
          <div className="wz-dialog-head">
            <h2 style={{ fontSize: 18 }}>{batch.name} · evaluation</h2>
            <Button iconOnly icon="x" aria-label="Close" onClick={onClose} />
          </div>
          <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <EvalFields strict={strict} hire={hire} duration={duration} onStrict={setStrict} onHire={setHire} onDuration={setDuration} />
            {error && <div className="wz-error" role="alert">{error}</div>}
            <p className="wz-sub" style={{ lineHeight: 1.5 }}>
              Applies to this batch only. A new hire score re-labels candidates who already finished; a new
              strictness applies to answers scored from now on. Interviews end when the duration runs out,
              including ones already in progress.
            </p>
          </div>
          <div className="wz-dialog-foot" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button need="exam.manage" variant="primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      </div>
    </>
  );
}

export function batchStats(data, hireBar, examSetId, batchId) {
  return statsFor(resultsForExamSet(data.people, examSetId), hireBar, (r) => r.batchId === batchId);
}

/** Prototype batchRows(): one sub-row per batch + a "New batch" row. */
export function BatchRows({ campaign, examSet, data, hireBar, onAdd, onReport, onNew }) {
  const list = data.batchesByExamSet[examSet.id] || [];
  // Batches whose candidate list is open (see BatchCandidates).
  const [openBatches, setOpenBatches] = useState(() => new Set());
  const [settingsFor, setSettingsFor] = useState(null);
  const toggle = (id) => setOpenBatches((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <>
      {list.map((b) => {
        const s = batchStats(data, hireBar, examSet.id, b.id);
        const open = openBatches.has(b.id);
        return (
          <React.Fragment key={b.id}>
          <div className={`wz-ivrow batch${open ? ' bopen' : ''}`}>
            <span className="btwist">
              <TwistButton expanded={open} label={`${open ? 'Hide' : 'Show'} candidates in ${b.name}`} onClick={() => toggle(b.id)} />
            </span>
            <span className="bname">
              <span style={{ fontWeight: 600 }}>{b.name}</span>
              <span className="wz-sub" style={{ fontSize: 12 }}>{batchWindow(b)}</span>
              <span className="wz-sub" style={{ fontSize: 12 }}>{batchEvalText(b)}</span>
            </span>
            <span /><span />
            <span>{s.total}</span>
            <Completion s={s} />
            <AvgChip value={s.avg} hireBar={s.hireBar} />
            <BatchStatus status={batchStatusOf(b)} />
            <div className="acts">
              <Button need="exam.manage" small icon="userAdd" disabled={batchStatusOf(b) === 'closed'} title={batchStatusOf(b) === 'closed' ? 'This batch is closed' : undefined} onClick={() => onAdd(examSet.id, b.id)}>Add</Button>
              <Button small onClick={() => onReport(examSet.id, b.id)}>Report</Button>
              <Button need="exam.manage" small icon="gear" iconOnly aria-label={`Evaluation settings for ${b.name}`} title="Grading strictness and hire score" onClick={() => setSettingsFor(b)} />
            </div>
          </div>
          {open && (
            <BatchCandidates campaign={campaign} examSet={examSet} batch={b} data={data} closed={batchStatusOf(b) === 'closed'} />
          )}
          </React.Fragment>
        );
      })}
      <div className="wz-ivrow addb">
        <span />
        <span style={{ paddingLeft: 46, display: 'flex', alignItems: 'center', gap: 12, gridColumn: '2 / -1' }}>
          <Button need="exam.manage" small variant="dashed" icon="plus" onClick={() => onNew(examSet.id)}>New batch</Button>
          {!list.length && <span className="wz-sub">No batches yet. Create one to invite candidates.</span>}
        </span>
      </div>
      {settingsFor && (
        <BatchSettingsDialog
          campaign={campaign}
          examSet={examSet}
          batch={settingsFor}
          onClose={() => setSettingsFor(null)}
          onSaved={() => { data.reloadBatches?.(); data.reloadCandidates?.(); }}
        />
      )}
    </>
  );
}

/** Prototype drawer() Batches tab: a card per batch + "New batch". */
export function BatchesTab({ examSet, data, hireBar, onAdd, onReport, onNew }) {
  const list = data.batchesByExamSet[examSet.id] || [];
  const [settingsFor, setSettingsFor] = useState(null);
  return (
    <>
      {data.batchesError && <div className="wz-error" role="alert">{data.batchesError}</div>}
      {list.length ? list.map((b) => {
        const s = batchStats(data, hireBar, examSet.id, b.id);
        const pct = s.total ? Math.round((s.done / s.total) * 100) : 0;
        const closed = batchStatusOf(b) === 'closed';
        return (
          <div key={b.id} className="wz-panel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <b style={{ fontSize: 15 }}>{b.name}</b>
                <span className="wz-sub">{batchWindow(b)}</span>
                <span className="wz-sub">{batchEvalText(b)}</span>
              </div>
              <BatchStatus status={batchStatusOf(b)} />
            </div>
            <div style={{ display: 'flex', gap: 18, fontSize: 13, color: 'var(--wz-muted)' }}>
              <span><b style={{ color: 'var(--wz-ink)' }}>{s.total}</b> candidate{s.total === 1 ? '' : 's'}</span>
              <span><b style={{ color: 'var(--wz-ink)' }}>{s.done}</b> completed</span>
              <span>Avg <b style={{ color: 'var(--wz-ink)' }}>{fmtScore(s.avg)}</b></span>
            </div>
            <ProgressBar pct={pct} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button need="exam.manage" small icon="userAdd" disabled={closed} title={closed ? 'This batch is closed' : undefined} onClick={() => onAdd(examSet.id, b.id)}>Add candidates</Button>
              <Button small onClick={() => onReport(examSet.id, b.id)}>Batch report</Button>
              <Button need="exam.manage" small icon="gear" onClick={() => setSettingsFor(b)}>Evaluation settings</Button>
            </div>
          </div>
        );
      }) : <p className="wz-sub">No batches yet.</p>}
      <Button need="exam.manage" variant="dashed" icon="plus" style={{ alignSelf: 'flex-start' }} onClick={() => onNew(examSet.id)}>New batch</Button>
      {settingsFor && (
        <BatchSettingsDialog
          campaign={data.campaign}
          examSet={examSet}
          batch={settingsFor}
          onClose={() => setSettingsFor(null)}
          onSaved={() => { data.reloadBatches?.(); data.reloadCandidates?.(); }}
        />
      )}
    </>
  );
}

/** Groups the panel's candidate rows by batch (prototype Candidates tab). */
export function groupEntriesByBatch(data, examSet, entries) {
  const list = data.batchesByExamSet[examSet.id] || [];
  const groups = list.map((b) => ({ key: b.id, label: b.name, list: entries.filter((e) => e.r.batchId === b.id) }));
  const known = new Set(list.map((b) => b.id));
  const rest = entries.filter((e) => !e.r.batchId || !known.has(e.r.batchId));
  if (rest.length) groups.push({ key: 'none', label: 'No batch', list: rest });
  return groups;
}

/**
 * Batch controls for the Add candidates dialog (prototype batchDialog()):
 * "Start a new batch" (name, window dates) or "Add to an existing batch".
 * Adding to a closed batch is blocked (T0 §5).
 */
export function useBatchChoice({ campaign, examSet, data, initialMode = 'new', initialBatchId = null }) {
  const list = useMemo(() => data.batchesByExamSet[examSet.id] || [], [data.batchesByExamSet, examSet.id]);
  const [mode, setMode] = useState(list.length ? initialMode : 'new');
  const [bid, setBid] = useState(initialBatchId || (list[list.length - 1] || {}).id || '');
  const [name, setName] = useState(`Batch ${list.length + 1}`);
  const [opens, setOpens] = useState(dateInput(0));
  const [closes, setCloses] = useState(dateInput(7));
  const defaults = newBatchDefaults(data, examSet.id);
  const [strict, setStrict] = useState(String(defaults.strict));
  const [hire, setHire] = useState(String(defaults.hire));
  const [duration, setDuration] = useState(String(defaults.duration));
  const isNew = mode === 'new' || !list.length;
  const chosen = list.find((b) => b.id === bid);
  const blocked = !isNew && chosen && batchStatusOf(chosen) === 'closed'
    ? 'This batch is closed. Pick an open or scheduled batch, or start a new one.'
    : (!isNew && !chosen ? 'Pick a batch.' : (isNew ? evalError(strict, hire, duration) : ''));

  const section = (
    <>
      {list.length > 0 && (
        <div className="wz-radio compact" role="radiogroup" aria-label="Batch">
          <label><input type="radio" name="bm" checked={isNew} onChange={() => setMode('new')} />Start a new batch</label>
          <label><input type="radio" name="bm" checked={!isNew} onChange={() => setMode('existing')} />Add to an existing batch</label>
        </div>
      )}
      {isNew ? (
        <>
          <div className="wz-batch-grid">
            <label className="wz-field"><span className="wz-sub">Batch name</span><input value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label className="wz-field"><span className="wz-sub">Opens</span><input type="date" value={opens} onChange={(e) => setOpens(e.target.value)} /></label>
            <label className="wz-field"><span className="wz-sub">Closes</span><input type="date" value={closes} onChange={(e) => setCloses(e.target.value)} /></label>
          </div>
          <EvalFields strict={strict} hire={hire} duration={duration} onStrict={setStrict} onHire={setHire} onDuration={setDuration} />
        </>
      ) : (
        <label className="wz-field">
          <span className="wz-sub">Batch</span>
          <select value={bid} onChange={(e) => setBid(e.target.value)}>
            {list.map((b) => (
              <option key={b.id} value={b.id}>{b.name} ({batchWindow(b)}, {BATCH_STATUS[batchStatusOf(b)][0].toLowerCase()})</option>
            ))}
          </select>
          {chosen && <span className="wz-sub" style={{ fontSize: 12 }}>{batchEvalText(chosen)}</span>}
        </label>
      )}
    </>
  );

  const onBeforeAssign = async () => {
    if (!isNew) return { batchId: bid };
    const res = await batchAPI.create(campaign.id, examSet.id, {
      name: name.trim() || `Batch ${list.length + 1}`,
      opens_at: opens ? new Date(`${opens}T00:00:00`).toISOString() : null,
      closes_at: closes ? new Date(`${closes}T23:59:59`).toISOString() : null,
      grading_leniency: 10 - clampInt(strict, 0, 10),
      min_hire_score: clampInt(hire, 0, 100),
      duration_minutes: clampInt(duration, 5, 240),
    });
    return { batchId: res.data?.id, created: true };
  };

  const submitLabel = (sendNow) => (isNew
    ? (sendNow ? 'Create batch and send invites' : 'Create batch and add')
    : (sendNow ? 'Add and send invites' : 'Add to batch'));

  return { section, blocked, onBeforeAssign, submitLabel, isNew };
}

/** Add candidates dialog with the batch controls on top (flag on). */
export function BatchAddCandidatesDialog({ mode, batchId, ...props }) {
  const c = useBatchChoice({ campaign: props.campaign, examSet: props.examSet, data: props.data, initialMode: mode, initialBatchId: batchId });
  return (
    <AddCandidatesDialog
      {...props}
      batchSection={c.section}
      blocked={c.blocked}
      onBeforeAssign={c.onBeforeAssign}
      submitLabel={c.submitLabel}
      allowEmpty={c.isNew}
    />
  );
}
